export const generateVideoSDKDemoHTML = (appId: string, secretKey: string): string => {
  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Simple VideoSDK Integration</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            max-width: 800px;
            margin: 50px auto;
            padding: 20px;
            background-color: #f0f2f5;
        }
        .container {
            background: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        h1 {
            text-align: center;
            color: #333;
            margin-bottom: 30px;
        }
        .form-group {
            margin-bottom: 20px;
        }
        label {
            display: block;
            margin-bottom: 8px;
            font-weight: bold;
            color: #555;
        }
        input {
            width: 100%;
            padding: 12px;
            border: 2px solid #ddd;
            border-radius: 6px;
            box-sizing: border-box;
            font-size: 16px;
        }
        input:focus {
            border-color: #007bff;
            outline: none;
        }
        button {
            background: #007bff;
            color: white;
            padding: 12px 30px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 16px;
            margin: 10px 10px 10px 0;
            transition: background-color 0.3s;
        }
        button:hover {
            background: #0056b3;
        }
        button:disabled {
            background: #ccc;
            cursor: not-allowed;
        }
        .success {
            background: #28a745;
        }
        .success:hover {
            background: #1e7e34;
        }
        .status {
            padding: 15px;
            margin: 20px 0;
            border-radius: 6px;
            font-weight: bold;
        }
        .status.success {
            background: #d4edda;
            color: #155724;
            border: 1px solid #c3e6cb;
        }
        .status.error {
            background: #f8d7da;
            color: #721c24;
            border: 1px solid #f5c6cb;
        }
        .status.info {
            background: #d1ecf1;
            color: #0c5460;
            border: 1px solid #bee5eb;
        }
        .results {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            padding: 15px;
            margin-top: 20px;
            min-height: 100px;
            white-space: pre-wrap;
            font-family: 'Courier New', monospace;
            font-size: 14px;
            max-height: 300px;
            overflow-y: auto;
        }
        .meeting-info {
            background: #e7f3ff;
            border: 1px solid #b3d9ff;
            border-radius: 6px;
            padding: 15px;
            margin: 20px 0;
        }
        .meeting-info h3 {
            margin-top: 0;
            color: #0066cc;
        }
        .copy-btn {
            background: #6c757d;
            font-size: 14px;
            padding: 5px 10px;
        }
        .copy-btn:hover {
            background: #545b62;
        }
        .method-section {
            border: 2px solid #e9ecef;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
            background: #f8f9fa;
        }
        .method-section h3 {
            margin-top: 0;
            color: #495057;
        }
        .scheduled-meeting-info {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 6px;
            padding: 15px;
            margin: 15px 0;
        }
        .join-section {
            background: #d1ecf1;
            border: 1px solid #bee5eb;
            border-radius: 6px;
            padding: 15px;
            margin: 15px 0;
        }
        .warning {
            background: #ffc107;
            color: #333;
        }
        .warning:hover {
            background: #d39e00;
        }
        .secondary {
            background: #6c757d;
        }
        .secondary:hover {
            background: #545b62;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>🎥 Peer Video Call SDK Integration</h1>
        
        <!-- Host Details Section -->
        <div class="form-group">
            <h3>👤 Host Details (You)</h3>
            <div class="form-group">
                <label for="userName">Your Name: <span style="color: red;">*</span></label>
                <input type="text" id="userName" placeholder="Enter your name" value="John Doe" required>
            </div>
            
            <div class="form-group">
                <label for="userEmail">Your Email (Optional):</label>
                <input type="email" id="userEmail" placeholder="Enter your email (optional)" value="john@example.com">
            </div>
            
            <div class="form-group">
                <label for="userMobile">Your Mobile (Optional):</label>
                <input type="tel" id="userMobile" placeholder="Enter your mobile number (optional)" value="1234567890">
            </div>
        </div>

        <!-- Second Participant Details -->
        <div class="form-group">
            <h3>👥 Second Participant Details (Required for Peer Call)</h3>
            <div class="form-group">
                <label for="participant2Name">Participant Name: <span style="color: red;">*</span></label>
                <input type="text" id="participant2Name" placeholder="Enter participant name" value="Jane Smith" required>
            </div>
            
            <div class="form-group">
                <label for="participant2Email">Participant Email (Optional):</label>
                <input type="email" id="participant2Email" placeholder="Enter participant email (optional)" value="jane@example.com">
            </div>
            
            <div class="form-group">
                <label for="participant2Mobile">Participant Mobile (Optional):</label>
                <input type="tel" id="participant2Mobile" placeholder="Enter participant mobile (optional)" value="0987654321">
            </div>
        </div>

        <!-- Method 1: Schedule Meeting -->
        <div class="method-section">
            <h3>📅 Method 1: Schedule Meeting</h3>
            <p>Schedule a meeting for future use. Returns a Session ID that can be used later to start the meeting.</p>
            
            <div class="form-group">
                <label for="meetingTime">Meeting Start Time:</label>
                <input type="datetime-local" id="meetingTime">
            </div>
            
            <div class="form-group">
                <label for="meetingDuration">Duration (minutes):</label>
                <input type="number" id="meetingDuration" value="60" min="15" max="480">
            </div>
            
            <div class="form-group">
                <label for="redirectUrl">Redirect URL (Optional):</label>
                <input type="url" id="redirectUrl" placeholder="Enter redirect URL (e.g., https://example.com)" value="https://notegtechnologies.com">
            </div>
            
            <button onclick="scheduleMeeting()" class="warning" id="scheduleBtn" disabled>📅 Schedule Meeting</button>
            
            <div id="scheduledMeetingInfo" class="scheduled-meeting-info" style="display: none;">
                <h4>📋 Scheduled Meeting Info</h4>
                <p><strong>Session ID:</strong> <span id="sessionId"></span> 
                   <button class="copy-btn" onclick="copySessionId()">Copy</button>
                </p>
                <p><em>Use this Session ID to start the meeting when ready.</em></p>
            </div>
        </div>

        <!-- Method 2: Start Meeting -->
        <div class="method-section">
            <h3>▶️ Method 2: Start Scheduled Meeting</h3>
            <p>Start a previously scheduled meeting using Session ID.</p>
            
            <div class="join-section">
                <div class="form-group">
                    <label for="roomId">Session ID / Room ID:</label>
                    <input type="text" id="roomId" placeholder="Enter Session ID from scheduled meeting">
                </div>
                
                <button onclick="startScheduledMeeting()" class="secondary" id="startScheduledBtn" disabled>▶️ Join Scheduled Meeting</button>
            </div>
        </div>
        
        <!-- Status Display -->
        <div id="status"></div>
        
        <!-- Meeting Info Display -->
        <div id="meetingInfo" style="display: none;"></div>
        
        <!-- Results Log -->
        <div class="form-group">
            <label>Activity Log:</label>
            <div id="results" class="results">Ready to start meeting...</div>
        </div>
    </div>

    <!-- Include the SDK -->
    <script src="./test/serviceBundle.js"></script>

    <script>
        // Your SDK credentials - Customized for your application
        const APP_ID = "${appId}";
        const SECRET_KEY = "${secretKey}";
        
        // SDK service instance
        let videoService = null;
        let isInitialized = false;
        let scheduledSessionId = null;

        // Set default meeting time to 1 hour from now
        window.addEventListener('load', function() {
            const now = new Date();
            const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
            document.getElementById('meetingTime').value = oneHourLater.toISOString().slice(0, 16);
            initializeSDK();
        });

        // Log messages to results area
        function log(message) {
            const resultsDiv = document.getElementById('results');
            const timestamp = new Date().toLocaleTimeString();
            const logEntry = \`[\${timestamp}] \${message}\\n\`;
            
            if (resultsDiv.textContent === 'Ready to start meeting...') {
                resultsDiv.textContent = '';
            }
            
            resultsDiv.textContent += logEntry;
            resultsDiv.scrollTop = resultsDiv.scrollHeight;
            console.log(message);
        }

        // Show status message
        function showStatus(message, type = 'info') {
            const statusDiv = document.getElementById('status');
            statusDiv.innerHTML = \`<div class="status \${type}">\${message}</div>\`;
        }

        // Initialize the VideoSDK
        function initializeSDK() {
            log('🔄 Initializing VideoSDK...');
            showStatus('Initializing SDK...', 'info');

            try {
                // Create SDK service instance
                if (window.VideoSDKService) {
                    videoService = new window.VideoSDKService();
                    log('✅ SDK service instance created');
                    
                    // Initialize with credentials
                    videoService.init(APP_ID, SECRET_KEY)
                        .then(response => {
                            isInitialized = true;
                            log('✅ SDK initialized successfully!');
                            showStatus('SDK Ready! All methods are now available.', 'success');
                            
                            // Enable all buttons
                            document.getElementById('scheduleBtn').disabled = false;
                            document.getElementById('startScheduledBtn').disabled = false;
                        })
                        .catch(error => {
                            log(\`❌ SDK initialization failed: \${error.message}\`);
                            showStatus(\`Initialization Failed: \${error.message}\`, 'error');
                        });
                } else {
                    throw new Error('VideoSDKService not found');
                }
            } catch (error) {
                log(\`❌ Failed to create SDK instance: \${error.message}\`);
                showStatus(\`SDK Loading Failed: \${error.message}\`, 'error');
            }
        }

        // Method 1: Schedule a meeting
        function scheduleMeeting() {
            if (!isInitialized || !videoService) {
                showStatus('SDK not initialized. Please wait...', 'error');
                return;
            }

            // Get user input for host
            const userName = document.getElementById('userName').value.trim();
            const userEmail = document.getElementById('userEmail').value.trim();
            const userMobile = document.getElementById('userMobile').value.trim();

            // Get user input for second participant
            const participant2Name = document.getElementById('participant2Name').value.trim();
            const participant2Email = document.getElementById('participant2Email').value.trim();
            const participant2Mobile = document.getElementById('participant2Mobile').value.trim();

            const meetingTime = document.getElementById('meetingTime').value;
            const duration = parseInt(document.getElementById('meetingDuration').value);
            const redirectUrl = document.getElementById('redirectUrl').value.trim();

            // Validate mandatory fields (only names and meeting details are required)
            if (!userName || !participant2Name || !meetingTime || !duration) {
                showStatus('Please fill in both participant names, meeting time, and duration', 'error');
                return;
            }

            // Disable button and show loading
            const scheduleBtn = document.getElementById('scheduleBtn');
            scheduleBtn.disabled = true;
            scheduleBtn.textContent = '🔄 Scheduling...';
            showStatus('Scheduling meeting with 2 participants...', 'info');

            log('📅 Scheduling meeting...');
            log(\`Host: \${userName} (\${userEmail || 'no email'}, \${userMobile || 'no mobile'})\`);
            log(\`Participant: \${participant2Name} (\${participant2Email || 'no email'}, \${participant2Mobile || 'no mobile'})\`);
            log(\`Scheduled for: \${new Date(meetingTime).toLocaleString()}\`);
            log(\`Duration: \${duration} minutes\`);
            log(\`Redirect URL: \${redirectUrl || 'none'}\`);

            // Calculate meeting times
            const startTime = new Date(meetingTime);
            const endTime = new Date(startTime.getTime() + duration * 60 * 1000);

            // Format dates to match your specification: "2025-08-06T11:50:06.558+00:00"
            // Preserve the local time selected by user (don't convert to UTC)
            const formatDateTime = (date) => {
                // Get the local date components
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const seconds = String(date.getSeconds()).padStart(2, '0');
                const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
                
                // Format as: "2025-08-06T13:10:00.000+00:00" (preserving local time)
                return \`\${year}-\${month}-\${day}T\${hours}:\${minutes}:\${seconds}.\${milliseconds}+00:00\`;
            };

            // Prepare meeting data with the exact JSON format you specified
            const meetingData = {
                grace_time: 30, // Using 30 minutes as per your specification
                participants: [
                    {
                        name: userName,
                        mobile_no: userMobile || '',
                        email_id: userEmail || '',
                        is_host: true
                    },
                    {
                        name: participant2Name,
                        mobile_no: participant2Mobile || '',
                        email_id: participant2Email || '',
                        is_host: false
                    }
                ],
                meeting_details: {
                    meeting_type: "Scheduled",
                    meeting_start_time: formatDateTime(startTime),
                    meeting_end_time: formatDateTime(endTime)
                },
                redirect_url: redirectUrl || 'https://notegtechnologies.com'
            };

            log(\`📋 Meeting data: \${JSON.stringify(meetingData, null, 2)}\`);

            // Schedule meeting
            videoService.scheduleMeeting(meetingData)
                .then(response => {
                    log('✅ Meeting scheduled successfully!');
                    log(\`Response: \${JSON.stringify(response, null, 2)}\`);
                    
                    if (response.data && response.data.sessionID) {
                        scheduledSessionId = response.data.sessionID;
                        
                        log(\`📋 Session ID: \${scheduledSessionId}\`);
                        log(\`⏰ Scheduled Start Time: \${startTime.toLocaleString()}\`);
                        log(\`⏰ Scheduled End Time: \${endTime.toLocaleString()}\`);
                        log(\`⏱️ Duration: \${duration} minutes\`);
                        log(\`👥 Participants: \${userName} (host) and \${participant2Name}\`);
                        log(\`🔗 Redirect URL: \${redirectUrl || 'https://notegtechnologies.com'}\`);
                        log(\`⏰ Grace Time: 30 minutes\`);
                        
                        // Show scheduled meeting info with start time object
                        showScheduledMeetingInfo(scheduledSessionId, startTime, duration);
                        
                        // Auto-fill the room ID in the start meeting section
                        document.getElementById('roomId').value = scheduledSessionId;
                        
                        showStatus(\`Peer meeting scheduled successfully! Start: \${startTime.toLocaleString()} | End: \${endTime.toLocaleString()}\`, 'success');
                    } else {
                        throw new Error('No Session ID received from server');
                    }
                    
                    // Reset button
                    scheduleBtn.textContent = '📅 Schedule Another Meeting';
                    scheduleBtn.disabled = false;
                })
                .catch(error => {
                    log(\`❌ Failed to schedule meeting: \${error.message}\`);
                    showStatus(\`Failed to schedule meeting: \${error.message}\`, 'error');
                    
                    // Reset button
                    scheduleBtn.textContent = '📅 Schedule Meeting';
                    scheduleBtn.disabled = false;
                });
        }

        // Method 2: Start a scheduled meeting
        function startScheduledMeeting() {
            if (!isInitialized || !videoService) {
                showStatus('SDK not initialized. Please wait...', 'error');
                return;
            }

            // Get user input
            const userName = document.getElementById('userName').value.trim();
            const userEmail = document.getElementById('userEmail').value.trim();
            const userMobile = document.getElementById('userMobile').value.trim();
            const roomId = document.getElementById('roomId').value.trim();

            // Validate mandatory fields (only name and roomId are required)
            if (!userName || !roomId) {
                showStatus('Please enter your name and Session ID', 'error');
                return;
            }

            // Disable button and show loading
            const startScheduledBtn = document.getElementById('startScheduledBtn');
            startScheduledBtn.disabled = true;
            startScheduledBtn.textContent = '🔄 Starting Meeting...';
            showStatus('Starting scheduled peer meeting...', 'info');

            log('▶️ Starting scheduled meeting...');
            log(\`Host: \${userName} (\${userEmail || 'no email'}, \${userMobile || 'no mobile'})\`);
            log(\`Session ID: \${roomId}\`);

            // Prepare start data (email and mobile are optional)
            const startData = {
                room_id: roomId,
                user_id: userEmail || userName, // Use email if provided, otherwise use name
                email_id: userEmail || '',
                mobile_no: userMobile || '',
                is_host: "true"
            };

            log(\`📋 Start data: \${JSON.stringify(startData, null, 2)}\`);

            // Start meeting
            videoService.startMeeting(startData)
                .then(response => {
                    log('✅ Scheduled peer meeting started successfully!');
                    log(\`Response: \${JSON.stringify(response, null, 2)}\`);
                    
                    if (response.data) {
                        const meetingUrl = response.data.meeting_url;
                        const sessionId = response.data.session_id;
                        
                        log(\`🎉 Peer meeting is now active!\`);
                        log(\`🏠 Room ID: \${roomId}\`);
                        if (meetingUrl) {
                            log(\`🔗 Meeting URL: \${meetingUrl}\`);
                        }
                        if (sessionId) {
                            log(\`📋 Active Session ID: \${sessionId}\`);
                        }
                        
                        // Show meeting info
                        showMeetingInfo(roomId, meetingUrl, sessionId || roomId, 'Scheduled Peer Meeting Started');
                        showStatus('Scheduled peer meeting started successfully! 🎉', 'success');
                    }
                    
                    // Reset button
                    startScheduledBtn.textContent = '▶️ Start Another Scheduled Meeting';
                    startScheduledBtn.disabled = false;
                })
                .catch(error => {
                    log(\`❌ Failed to start scheduled meeting: \${error.message}\`);
                    showStatus(\`Failed to start meeting: \${error.message}\`, 'error');
                    
                    // Reset button
                    startScheduledBtn.textContent = '▶️ Join Scheduled Meeting';
                    startScheduledBtn.disabled = false;
                });
        }

        // Show scheduled meeting information
        function showScheduledMeetingInfo(sessionId, startTime, duration) {
            const scheduledInfoDiv = document.getElementById('scheduledMeetingInfo');
            const redirectUrl = document.getElementById('redirectUrl').value.trim();
            
            // Calculate end time
            const endTime = new Date(startTime.getTime() + duration * 60 * 1000);
            
            // Update the HTML to include start and end times
            scheduledInfoDiv.innerHTML = \`
                <h4>📋 Scheduled Meeting Info</h4>
                <p><strong>Session ID:</strong> <span id="sessionId">\${sessionId}</span> 
                   <button class="copy-btn" onclick="copySessionId()">Copy</button>
                </p>
                <p><strong>Meeting Start Time:</strong> \${startTime.toLocaleString()}</p>
                <p><strong>Meeting End Time:</strong> \${endTime.toLocaleString()}</p>
                <p><strong>Duration:</strong> \${duration} minutes</p>
                <p><strong>Grace Time:</strong> 30 minutes</p>
                <p><strong>Time Zone:</strong> \${Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
                <p><strong>Redirect URL:</strong> <a href="\${redirectUrl || 'https://notegtechnologies.com'}" target="_blank">\${redirectUrl || 'https://notegtechnologies.com'}</a></p>
                <p><em>Use this Session ID to start the meeting when ready.</em></p>
            \`;
            
            scheduledInfoDiv.style.display = 'block';
            
            // Log the detailed times
            log(\`⏰ Meeting Start Time: \${startTime.toLocaleString()}\`);
            log(\`⏰ Meeting End Time: \${endTime.toLocaleString()}\`);
            log(\`⏱️ Total Duration: \${duration} minutes\`);
            log(\`⏰ Grace Time: 30 minutes\`);
            log(\`🌍 Time Zone: \${Intl.DateTimeFormat().resolvedOptions().timeZone}\`);
            log(\`🔗 Redirect URL: \${redirectUrl || 'https://notegtechnologies.com'}\`);
        }

        // Copy session ID
        function copySessionId() {
            const sessionId = document.getElementById('sessionId').textContent;
            copyToClipboard(sessionId);
        }

        // Show meeting information
        function showMeetingInfo(roomId, meetingUrl, sessionId, meetingType = 'Meeting') {
            const meetingInfoDiv = document.getElementById('meetingInfo');
            
            let infoHTML = \`
                <h3>🎉 \${meetingType} Created Successfully!</h3>
                <p><strong>Room ID:</strong> \${roomId} 
                   <button class="copy-btn" onclick="copyToClipboard('\${roomId}')">Copy</button>
                </p>
            \`;
            
            if (meetingUrl) {
                infoHTML += \`
                    <p><strong>Meeting URL:</strong> 
                       <a href="\${meetingUrl}" target="_blank">\${meetingUrl}</a>
                       <button class="copy-btn" onclick="copyToClipboard('\${meetingUrl}')">Copy</button>
                    </p>
                \`;
            }
            
            if (sessionId) {
                infoHTML += \`
                    <p><strong>Session ID:</strong> \${sessionId}
                       <button class="copy-btn" onclick="copyToClipboard('\${sessionId}')">Copy</button>
                    </p>
                \`;
            }
            
            infoHTML += \`
                <p><em>Share the Room ID or Meeting URL with participants to join the meeting.</em></p>
            \`;
            
            meetingInfoDiv.innerHTML = infoHTML;
            meetingInfoDiv.style.display = 'block';
        }

        // Copy text to clipboard
        function copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                log(\`📋 Copied to clipboard: \${text}\`);
                showStatus('Copied to clipboard!', 'success');
                setTimeout(() => {
                    if (isInitialized) {
                        showStatus('SDK Ready! All methods are now available.', 'success');
                    }
                }, 2000);
            }).catch(err => {
                log(\`❌ Failed to copy: \${err.message}\`);
            });
        }

        // Log initial messages
        log('🌟 Peer Video Call SDK Integration - Schedule & Start Methods');
        log('📋 Two methods available:');
        log('   📅 Schedule Meeting - Schedule peer meeting for later use');
        log('   ▶️ Start Scheduled Meeting - Join a scheduled peer meeting');
        log('⚠️  IMPORTANT: This is a peer video call SDK - requires exactly 2 participants');
        log('📝 Names are mandatory, email and mobile are optional');
        log('---');
    </script>
</body>
</html>`;
};
